//changed due to package upgrade
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import jwtAxios from '@projectK/services/auth/JWT';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { FaCalendarAlt, FaComment } from 'react-icons/fa';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate } from 'react-router-dom';
import { IoMdCheckboxOutline } from 'react-icons/io';
import {
  formatStartDate,
  formatTime,
  formatDateOnly,
  timeZoneDate,
} from '@projectK/modules/apps/DateFormat';
import AdminCalendar from '../../../../../../../libs/modules/src/lib/admin/CalendarActivity/AdminCalendar';
import AppLoader from '@projectK/components/AppLoader';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { FiFileText } from 'react-icons/fi';
import GroupsIcon from '@mui/icons-material/Groups';
import EqualizerOutlinedIcon from '@mui/icons-material/EqualizerOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import { FaCalendarCheck } from 'react-icons/fa6';

const quickActions = [
  {
    title: <IntlMessages id='coachQuickActions.takeAttendance.title' />,
    subtitle: <IntlMessages id='coachQuickActions.takeAttendance.subtitle' />,
    icon: <IoMdCheckboxOutline fontSize='large' />,
    bgcolor: '#4ade80',
    color: '#fff',
    link: '/coach/activity-session',
  },
  {
    title: <IntlMessages id='coachQuickActions.viewSessions.title' />,
    subtitle: <IntlMessages id='coachQuickActions.viewSessions.subtitle' />,
    icon: <CalendarTodayOutlinedIcon fontSize='large' />,
    bgcolor: '#3c83f6',
    color: '#fff',
    link: '/coach/calendar',
  },
  {
    title: <IntlMessages id='coachQuickActions.memberSearch.title' />,
    subtitle: <IntlMessages id='coachQuickActions.memberSearch.subtitle' />,
    icon: <SearchIcon fontSize='large' />,
    bgcolor: '#2a9d90',
    color: '#fff',
    link: '/coach/users',
  },
  {
    title: <IntlMessages id='coachQuickActions.sendEmails.title' />,
    subtitle: <IntlMessages id='coachQuickActions.sendEmails.subtitle' />,
    icon: <ChatBubbleOutlineOutlinedIcon fontSize='large' />,
    bgcolor: '#7c3bed',
    color: '#fff',
    link: '/coach/communication',
  },
  {
    title: <IntlMessages id='coachQuickActions.sessionWaitlist.title' />,
    subtitle: <IntlMessages id='coachQuickActions.sessionWaitlist.subtitle' />,
    icon: <ChecklistIcon fontSize='large' />,
    bgcolor: '#ec4699',
    color: '#fff',
    link: '/coach/my-waitlist',
  },
  {
    title: <IntlMessages id='coachQuickActions.userReports.title' />,
    subtitle: <IntlMessages id='coachQuickActions.userReports.subtitle' />,
    icon: <FiFileText fontSize='large' />,
    bgcolor: '#6467f2',
    color: '#fff',
    link: '/coach/user-reports',
  },
];

const CoachDashboard = () => {
  const navigate = useNavigate();
  const todayDate = timeZoneDate().todayDate;
  const [view, setView] = React.useState('list');
  const { user } = useAuthUser();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [useDateFilter] = useState(true);
  const [page, setPage] = useState(0);
  const [perPage] = useState(5);

  const abortControllerRef = useRef(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const { startDate, endDate } = useMemo(() => {
    const sessionDate = new Date(currentDate); // Use the currentDate for the session date

    // Start date: First day of the current month
    const start = new Date(
      sessionDate.getFullYear(),
      sessionDate.getMonth(),
      1,
    );

    // End date: Last day of the current month
    const end = new Date(
      sessionDate.getFullYear(),
      sessionDate.getMonth() + 1,
      0,
    );

    return {
      startDate: start.toISOString().split('T')[0], // Format as YYYY-MM-DD
      endDate: end.toISOString().split('T')[0], // Format as YYYY-MM-DD
    };
  }, [currentDate]);

  const handleMonthChange = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate); // Update to the new date including year changes
  };

  useEffect(() => {
    fetchData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    useDateFilter,
    view,
    startDate, // New parameter
    endDate, // New parameter
  ]);

  const fetchData = async (
    pageNum = page,
    perPageNum = perPage,
    append = false,
  ) => {
    try {
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      append ? setLoadingMore(true) : setLoading(true);

      const response = await jwtAxios.get(`/activity-session`, {
        params: {
          page: pageNum,
          perPage: view === 'calendar' ? 'all' : perPageNum,
          startDate: view === 'calendar' ? startDate : null, // New parameter
          endDate: view === 'calendar' ? endDate : null, // New parameter
          useDateFilter,
          filters: { instructorId: user?.id },
        },
        signal,
      });

      const { data } = response;
      if (append) {
        setList((prev) => [...prev, ...(data?.list || [])]);
      } else {
        setList(data?.list || []);
      }

      setTotal(data?.total);
    } catch (error) {
      console.error(error);
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  };

  const [next7DaysCount, setNext7DaysCount] = useState(0);
  const [percentChange, setPercentChange] = useState(0);
  const [playersAttendingCount, setPlayersAttendingCount] = useState(0);
  const [playersAttendingPercentage, setPlayersAttendingPercentage] =
    useState(0);
  const [pendingAttendanceCount, setPendingAttendanceCount] = useState(0);
  const [attendanceRate, setAttendanceRate] = useState(0);
  const [attendancePercentageChange, setAttendancePercentageChange] =
    useState(0);

  const fetchAllStatsParallel = async () => {
    try {
      const [sessionsRes, playersRes, pendingRes, attendanceRate] =
        await Promise.all([
          jwtAxios.get(
            `/dashboard/sessions-assigned/count-instructor/${user?.id}`,
          ),
          jwtAxios.get(`/dashboard/players-attending/count/${user?.id}`),
          jwtAxios.get(`/dashboard/pending-attendance/count/${user?.id}`),
          jwtAxios.get(`/dashboard/attendance-rate/percentage/${user?.id}`),
        ]);

      setNext7DaysCount(sessionsRes?.data?.next7DaysCount || 0);
      setPercentChange(sessionsRes?.data?.percentChange || 0);

      setPlayersAttendingCount(playersRes?.data?.count || 0);
      setPlayersAttendingPercentage(playersRes?.data?.percentageChange || 0);

      setPendingAttendanceCount(pendingRes?.data?.pendingCount || 0);

      setAttendanceRate(attendanceRate?.data?.attendancePercentage || 0);
      setAttendancePercentageChange(
        attendanceRate?.data?.percentageChange || 0,
      );
    } catch (error) {
      console.error('Error fetching multiple stats:', error);
    }
  };

  useEffect(() => {
    if (user?.id) fetchAllStatsParallel();
  }, [user?.id]);

  const stats = [
    {
      title: <IntlMessages id='coachStats.sessionsAssigned.title' />,
      value: next7DaysCount,
      sub1: <IntlMessages id='coachStats.sessionsAssigned.sub1' />,
      sub: `${percentChange}% more than last week`,
      bgcolor: '#dbeafe',
      color: '#2362eb',
      subcolor: '#12a146',
      icon: <FaCalendarCheck fontSize='large' />,
      subicon: <ArrowUpwardIcon fontSize='small' />,
    },
    {
      title: <IntlMessages id='coachStats.playersAttending.title' />,
      value: playersAttendingCount,
      sub1: <IntlMessages id='coachStats.playersAttending.sub1' />,
      sub: `${playersAttendingPercentage}% less than last week`,
      bgcolor: '#dcfce7',
      color: '#12a146',
      subcolor: '#d32f2f',
      icon: <GroupsIcon fontSize='large' />,
      subicon: <ArrowDownwardIcon fontSize='small' />,
    },
    {
      title: <IntlMessages id='coachStats.pendingFeedback.title' />,
      value: pendingAttendanceCount,
      sub1: <IntlMessages id='coachStats.pendingFeedback.sub1' />,
      bgcolor: '#f3e8ff',
      color: '#9334ea',
      subcolor: '#f39c12',
      icon: <FaComment fontSize='medium' />,
    },
    {
      title: <IntlMessages id='coachStats.attendanceRate.title' />,
      value: `${attendanceRate}%`,
      sub1: <IntlMessages id='coachStats.attendanceRate.sub1' />,
      sub: `${attendancePercentageChange}% increase from last month`,
      bgcolor: '#ffeed4',
      color: '#f39c12',
      subcolor: '#12a146',
      icon: <EqualizerOutlinedIcon fontSize='large' />,
      subicon: <ArrowUpwardIcon fontSize='small' />,
    },
  ];

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchData(nextPage, perPage, true); // load next 10 sessions
  };

  const getSessionStatus = (start_date, start_time, end_date, end_time) => {
    const now = timeZoneDate().todayDate;
    const startDateString =
      start_date?.length >= 10 ? start_date.substring(0, 10) : start_date;
    const endDateString =
      end_date?.length >= 10 ? end_date.substring(0, 10) : end_date;

    const startDateTimeString = `${startDateString}T${start_time || '00:00'}`;
    const endDateTimeString = `${endDateString}T${end_time || '23:59'}`;

    const start = timeZoneDate(startDateTimeString).startDate;
    const end = timeZoneDate(endDateTimeString).startDate;

    const isToday = now.hasSame(start, 'day');
    if (!isToday && now < start)
      return { status: 'Upcoming', color: '#BFDBFE' };
    if (now > end) return { status: 'Completed', color: '#BBF7D0' };
    if (now >= start && now <= end)
      return { status: 'Ongoing', color: '#FEF9C3' }; // yellowish tone
    if (isToday) return { status: 'Today', color: '#FECACA' };
    return { status: 'Upcoming', color: '#BFDBFE' };
  };

  return (
    <Box className='admin-dashboard'>
      {/* Header */}
      <Box
        className='headerbg'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
      >
        <Box>
          <Typography variant='h1'>
            <IntlMessages id='welcomeBack' /> {user?.first_name}{' '}
            {user?.last_name}
          </Typography>
          <Typography sx={{ color: '#eee', mb: 3 }}>
            <IntlMessages id='coachDashBoardTitle' />
          </Typography>
        </Box>
        <Box>
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant='h2'>
              {' '}
              <IntlMessages id='common.today' />
            </Typography>
            <Typography>{todayDate.toFormat('MMMM dd, yyyy')}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats Section */}
      <Grid container spacing={{ xs: 2, md: 5 }} className='stats'>
        {stats.map((item, index) => (
          <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index + 1}>
            <Card sx={{ border: 1, borderColor: item.color }}>
              <CardContent>
                <Box className='title_icon'>
                  <Typography variant='subtitle2'>{item.title}</Typography>
                  <Box sx={{ bgcolor: item.bgcolor, color: item.color }}>
                    {item.icon}
                  </Box>
                </Box>
                <Typography
                  variant='h4'
                  sx={{ fontWeight: 'bold', mt: 1, color: '#000' }}
                >
                  {item.value}
                </Typography>
                <Typography className='sub_item1' variant='subtitle1'>
                  {item.sub1}
                </Typography>
                <Box className='sub_item' sx={{ bgcolor: '#fbfbfb' }}>
                  <Typography variant='subtitle1' sx={{ color: item.subcolor }}>
                    {item.subicon} {item.sub}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Box className='quick-actions'>
        <Typography
          variant='h2'
          sx={{
            fontWeight: 'bold',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <StarOutlineIcon fontSize='large' color='primary' />
          <IntlMessages id='coachTools' />
        </Typography>
        <Grid container spacing={{ xs: 2, md: 5 }}>
          {quickActions.map((action, index) => (
            <Grid size={{ xs: 6, sm: 6, md: 2 }} key={index + 1}>
              <Card
                sx={{
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                  transition: '0.3s',
                }}
                onClick={() => navigate(action.link)}
              >
                <Box sx={{ bgcolor: action.bgcolor, color: action.color }}>
                  {action.icon}
                </Box>
                <Typography variant='h3' fontWeight='500'>
                  {action.title}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {action.subtitle}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Recent Activity */}
      <Grid
        container
        spacing={{ xs: 2, md: 5 }}
        className='activity-updates'
        mb={3}
      >
        <Grid size={{ xs: 12, md: 12 }}>
          <Box className='recent-activity session_boxes' mb={5}>
            <Box display='flex' justifyContent='space-between' mb={2}>
              <Typography
                variant='h2'
                sx={{
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CalendarTodayOutlinedIcon color='primary' />
                <IntlMessages id='mySessions' />
              </Typography>
              <ToggleButtonGroup
                size='small'
                exclusive
                value={view}
                onChange={(event, newView) => {
                  if (newView !== null) setView(newView);
                }}
              >
                <ToggleButton value='list'>
                  <FormatListBulletedIcon />
                </ToggleButton>
                <ToggleButton value='calendar'>
                  <FaCalendarAlt />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Card>
              <CardContent>
                {(() => {
                  if (loading) {
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          py: 4,
                        }}
                      >
                        <AppLoader />
                      </Box>
                    );
                  } else if (view === 'list') {
                    if (list.length > 0) {
                      return list.map((session, idx) => {
                        const { status, color } = getSessionStatus(
                          session.start_date,
                          session.start_time,
                          session.end_date,
                          session.end_time,
                        );

                        return (
                          <Box
                            className='session_box'
                            key={idx + 1}
                            display='flex'
                            justifyContent='space-between'
                            alignItems='center'
                            px={2}
                            py={1.5}
                            sx={{
                              borderBottom: '1px solid #eee',
                              '&:hover': { bgcolor: '#f9f9f9' },
                            }}
                          >
                            <Box
                              className='day_date'
                              textAlign='center'
                              minWidth={90}
                            >
                              <Typography variant='subtitle2' fontWeight='bold'>
                                {formatStartDate(session.start_date).day}
                              </Typography>
                              <Typography
                                variant='subtitle2'
                                sx={{ whiteSpace: 'pre-line' }}
                              >
                                {formatDateOnly(session.start_date)}
                              </Typography>
                            </Box>
                            <Box className='session_details' flexGrow={1}>
                              <Typography variant='h3' fontWeight='500'>
                                {session.activity_display_name
                                  ? session.activity_display_name
                                  : session.activity_name}{' '}
                                - {session.product_name}
                              </Typography>
                              <Box display='flex' alignItems='center' gap={0.5}>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  <LocationOnIcon
                                    fontSize='small'
                                    color='action'
                                  />{' '}
                                  {session.address} •{' '}
                                  <AccessTimeIcon
                                    fontSize='small'
                                    color='action'
                                  />{' '}
                                  {formatTime(session.start_time)} -{' '}
                                  {formatTime(session.end_time)}
                                </Typography>
                              </Box>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                {session.user_session_count} players
                              </Typography>
                            </Box>
                            <Box className='session_status'>
                              <Typography
                                variant='body2'
                                fontWeight='500'
                                sx={{
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 2,
                                  backgroundColor: color,
                                  textAlign: 'center',
                                }}
                              >
                                {status}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      });
                    } else {
                      return (
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          align='center'
                          sx={{ textAlign: 'center', py: 3, fontWeight: '600' }}
                        >
                          <IntlMessages id='noUpcomingActivities' />
                        </Typography>
                      );
                    }
                  }

                  // view !== 'list' -> render calendar
                  else {
                    return (
                      <AdminCalendar
                        events={list}
                        onMonthChange={handleMonthChange}
                        currentDate={currentDate}
                        loading={loading}
                      />
                    );
                  }
                })()}
              </CardContent>

              {/* Load More */}
              <Box
                className='view_all'
                sx={{ alignItems: 'center', textAlign: 'center', pb: 3 }}
              >
                {(() => {
                  if (loadingMore) return <AppLoader />;
                  if (list.length < total) {
                    return (
                      <Button variant='text' onClick={handleLoadMore}>
                        <IntlMessages id='viewFullSchedule' />
                      </Button>
                    );
                  }
                  return null;
                })()}
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CoachDashboard;
